import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchMissionStatement } from "@/services/api/common";
import styles from "./SeeTheWorld.module.css";

const DEFAULT_TITLE = "See The World With Next Holidays";

const DEFAULT_BODY =
  "We've made it our mission to help you travel with confidence and make your journey as smooth as possible. " +
  "From the moment you start planning to the day you return home, Next Holidays is built to remove the stress from travel and put discovery first. " +
  "We carefully curate holiday packages, flights, stays, and activities so you can explore more of the world without worrying about the details. " +
  "Whether you are looking for a quick city break, a family vacation, a romantic getaway, or an adventure across continents, our experts design experiences that feel personal and practical. " +
  "Transparent pricing, trusted partners, and dedicated support mean you always know what to expect before you book. " +
  "Our destination specialists keep an eye on seasonal deals, visa requirements, and local highlights so your itinerary stays relevant and rewarding. " +
  "Book with peace of mind knowing that flexible options, clear inclusions, and real human assistance are part of every trip we help create. " +
  "Discover new cultures, landscapes, and memories with a travel partner that puts your comfort and clarity first—every route, every stay, every adventure.";

const LINE_CLAMP = 3;

function containsHtml(value = "") {
  return /<[^>]+>/.test(String(value));
}

function stripHtml(value = "") {
  const str = String(value);
  if (typeof document !== "undefined") {
    const el = document.createElement("div");
    el.innerHTML = str;
    return (el.textContent || "").replace(/\s+/g, " ").trim();
  }

  return str
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeMissionStatement(response) {
  const data = response?.data ?? response ?? {};

  const title =
    data.title ||
    data.heading ||
    data.missionTitle ||
    data.pageTitle ||
    data.name ||
    "";

  const text =
    data.missionStatement ||
    data.description ||
    data.content ||
    data.body ||
    data.statement ||
    data.text ||
    "";

  return {
    title: String(title).trim(),
    text: String(text).trim(),
  };
}

export default function SeeTheWorld({
  pageType,
  title: titleProp,
  text: textProp,
  lineClamp = LINE_CLAMP,
}) {
  const [missionData, setMissionData] = useState({ title: "", text: "" });
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const textRef = useRef(null);

  const loadMissionStatement = useCallback(() => {
    if (!pageType || titleProp || textProp) return Promise.resolve();

    return fetchMissionStatement(pageType)
      .then((response) => {
        setMissionData(normalizeMissionStatement(response));
      })
      .catch((error) => {
        console.error("Failed to load mission statement:", error);
      });
  }, [pageType, titleProp, textProp]);

  useEffect(() => {
    loadMissionStatement();
  }, [loadMissionStatement]);

  useEffect(() => {
    setExpanded(false);
  }, [textProp, missionData.text]);

  const title = titleProp || missionData.title || DEFAULT_TITLE;
  const text = textProp || missionData.text || DEFAULT_BODY;
  const isHtmlContent = containsHtml(text);
  const isHtmlTitle = containsHtml(title);
  const plainText = useMemo(() => stripHtml(text), [text]);

  useEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return undefined;

    const measureOverflow = () => {
      if (!textRef.current) return;
      const node = textRef.current;
      const overflowing = node.scrollHeight > node.clientHeight + 2;
      const longCopy = plainText.split(/\s+/).filter(Boolean).length > lineClamp * 16;
      setCanExpand(overflowing || longCopy || isHtmlContent);
    };

    const frame = window.requestAnimationFrame(measureOverflow);
    const lateFrame = window.setTimeout(measureOverflow, 80);

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measureOverflow);
      observer.observe(el);
    }

    window.addEventListener("resize", measureOverflow);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(lateFrame);
      observer?.disconnect();
      window.removeEventListener("resize", measureOverflow);
    };
  }, [expanded, isHtmlContent, lineClamp, plainText, text]);

  const clampStyle = {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: String(lineClamp),
    overflow: "hidden",
  };

  return (
    <div className="standard-padding">
      <section className={styles.section} aria-labelledby="see-the-world-title">
        {isHtmlTitle ? (
          <div
            id="see-the-world-title"
            className={`standard-heading ${styles.titleHtml}`}
            dangerouslySetInnerHTML={{ __html: title }}
          />
        ) : (
          <h2 id="see-the-world-title" className="standard-heading">
            {title}
          </h2>
        )}

        <div className={styles.bodyWrap}>
          {expanded && isHtmlContent ? (
            <div
              className={`${styles.body} ${styles.bodyHtml}`}
              dangerouslySetInnerHTML={{ __html: text }}
            />
          ) : expanded ? (
            <p className={styles.body}>{text}</p>
          ) : (
            <p
              ref={textRef}
              className={`${styles.body} ${styles.clamped}`}
              style={clampStyle}
            >
              {plainText}
            </p>
          )}

          {canExpand || expanded ? (
            <button
              type="button"
              className={`${styles.toggle} ${styles.toggleBlock}`}
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
            >
              {expanded ? "See less" : "See more"}
            </button>
          ) : null}
        </div>
      </section>
    </div>
  );
}
